import React, { useState, useCallback, useEffect, forwardRef, useImperativeHandle, useMemo, useRef, } from 'react';
import makeStyles from "@mui/styles/makeStyles";
import {
    Theme,
    Box,
    Button,
    Typography,
    Paper,
} from '@mui/material';
import { Template, ITemplateWorksheetColumnsProps } from '../../types/types';
import { ICellRendererParams, RowDragEndEvent } from 'ag-grid-community';
import SecuritiesDraggableTable, { IGridOptions, ISecuritiesTableFieldMap } from '../../components/Shared/SecuritiesDraggableTable/SecuritiesDraggableTable';
import ActionCellRenderer from '../../components/Shared/SecuritiesDraggableTable/ActionCellRenderer';
import CustomCellRenderer from '../../components/Shared/SecuritiesDraggableTable/CustomCellRenderer';
import ValidationInfoCellRenderer, { DEFAULT_VALIDATION_INFO_TRUNCATE_LENGTH } from '../../components/Shared/SecuritiesDraggableTable/ValidationInfoCellRenderer';
import NoteCellRenderer, { DEFAULT_NOTE_TRUNCATE_LENGTH } from '../../components/Shared/SecuritiesDraggableTable/NoteCellRenderer';
import NoteIndicatorCellRenderer from '../../components/Shared/SecuritiesDraggableTable/NoteIndicatorCellRenderer';
import { getInfoJsonWithArray } from '../../utilities/utilities';
import {
    getValidationInfoDisplayStrings,
    getValidationInfoDisplayText,
    IValidationInfoDisplayStrings,
    VALIDATION_INFO_DISPLAY_STRING_KEYS,
} from './validationInfoDisplay';
import { normalizeCustomName } from '../../utilities/utilities';

const useStyles = makeStyles((theme: Theme) => ({
    button: {
        background: "#006B86",
        size: "small",
        margin: theme.spacing(1),
        color: "white",
    },
    floatRight: {
        float: 'right',
    },

}));

export interface TemplateWorksheetColumnsRef {
    isDirty: boolean;
    getCurrentTemplate: () => Template | null;
    getCurrentOriginalTemplate: () => Template | null;
    reset: () => boolean;
}
interface TemplateWorksheetColumnsProps {
    templateWorksheetColumnsProps: ITemplateWorksheetColumnsProps;
}
interface IRowDataIdListOrder {
    id: number;
    order: number;
}

const TemplateWorksheetColumns = forwardRef<TemplateWorksheetColumnsRef, TemplateWorksheetColumnsProps>(
    ({ templateWorksheetColumnsProps }, ref) => {
        const classes = useStyles();
        const currentOriginalTemplate = templateWorksheetColumnsProps.incomingTemplate;
        const [currentTemplate, setCurrentTemplate] = useState<Template | null>(currentOriginalTemplate);
        // Tracks the incomingTemplate reference this component's state was last synced from, so we
        // can reset currentTemplate synchronously during render (rather than via a useEffect) when
        // the parent hands down a new template (e.g. right after Save). Doing this via an effect
        // would leave a render pass where worksheetColumns/rowData are still derived from the
        // stale currentTemplate, causing a brief "No columns found" flash before the effect runs.
        const [syncedOriginalTemplate, setSyncedOriginalTemplate] = useState(currentOriginalTemplate);
        if (currentOriginalTemplate !== syncedOriginalTemplate) {
            setSyncedOriginalTemplate(currentOriginalTemplate);
            setCurrentTemplate(currentOriginalTemplate);
        }
        const lastSavedTemplate = (JSON.parse(JSON.stringify(templateWorksheetColumnsProps.lastSavedTemplate)));
        const currentWorksheetId = templateWorksheetColumnsProps.currentWorksheetId;
        const onDirtyChange: Function = templateWorksheetColumnsProps.onDirtyChange;
        const handleOpenThirdDialog = templateWorksheetColumnsProps.handleOpenThirdDialog;
        const tableRef = useRef<any>(null);
        const [gridOptions, setGridOptions] = useState<IGridOptions>();
        const [rowData, setRowData] = useState<ISecuritiesTableFieldMap[]>([]);
        const [validationInfoDisplayStrings, setValidationInfoDisplayStrings] = useState<IValidationInfoDisplayStrings>(getValidationInfoDisplayStrings(null));

        // Fetch the Strings-table values used to construct the read-only Instructions/ValidationInfo
        // display text (see validationInfoDisplay.ts). This is display-only and never mutates the
        // real, persisted validationInfo value held in currentTemplate/state.
        useEffect(() => {
            let isMounted = true;

            getInfoJsonWithArray(VALIDATION_INFO_DISPLAY_STRING_KEYS).then(strings => {
                if (isMounted) {
                    setValidationInfoDisplayStrings(getValidationInfoDisplayStrings(strings));
                }
            });

            return () => { isMounted = false; };
        }, []);

        // Resync of local state to the parent's incomingTemplate happens synchronously above
        // (during render), not via an effect, to avoid a stale-render flash.

        const worksheetColumns = useMemo(() => {
            return (
                currentTemplate?.worksheets.find(worksheet => worksheet.id === currentWorksheetId)?.worksheetColumns ?? []
            ).slice().sort((a, b) => a.order - b.order);
        }, [currentTemplate, currentWorksheetId]);


        const buildRows = useCallback(() => {
            setRowData(() => {
                return worksheetColumns?.map(item => ({
                    id: { type: 'number', value: item.id },
                    order: { type: 'number', value: item.order },
                    customName: { type: 'string', value: item.customName },
                    name: { type: 'string', value: item.name },
                    fieldName: { type: 'string', value: item.fieldName },
                    mapToField: { type: 'string', value: item.mapToField },
                    fieldIsRequired: { type: 'boolean', value: item.fieldIsRequired },
                    // Display-only value; the real, persisted validationInfo on item/currentTemplate
                    // is never mutated by this computation, so Save always submits the true value.
                    // worksheetColumns is passed as siblings so TransactionDate (Trade Date) can reuse
                    // SecurityType's computed display text, per the generator's coupling rule.
                    validationInfoDisplay: { type: 'string', value: getValidationInfoDisplayText(item, validationInfoDisplayStrings, worksheetColumns) },
                    dataTypeId: { type: 'number', value: item.dataTypeId },
                    note: { type: 'string', value: item.note ?? '' },
                })) || [];
            });
        }, [worksheetColumns, validationInfoDisplayStrings]);

        // Handle custom name change
        const handleCustomNameChange = useCallback(
            (id: number, value: string) => {

                if (!currentTemplate) return;

                const normalizedValue = normalizeCustomName(value);

                setCurrentTemplate((prevTemplate) => {
                    if (!prevTemplate) return prevTemplate;

                    const updatedWorksheets = prevTemplate.worksheets.map(worksheet => {

                        if (worksheet.id !== currentWorksheetId) return worksheet;

                        const updatedColumns = worksheet.worksheetColumns?.map(column => {

                            if (column.id === id) {
                                return { ...column, customName: normalizedValue };
                            }

                            return column;

                        });

                        return {
                            ...worksheet,
                            worksheetColumns: updatedColumns
                        };
                    });
                    return {
                        ...prevTemplate,
                        worksheets: updatedWorksheets,
                    };
                });

            },
            [currentTemplate, currentWorksheetId]
        );

        // Handle note change
        const handleNoteChange = useCallback(
            (id: number, value: string) => {

                if (!currentTemplate) return;

                setCurrentTemplate((prevTemplate) => {
                    if (!prevTemplate) return prevTemplate;

                    const updatedWorksheets = prevTemplate.worksheets.map(worksheet => {

                        if (worksheet.id !== currentWorksheetId) return worksheet;

                        const updatedColumns = worksheet.worksheetColumns?.map(column => {

                            if (column.id === id) {
                                return { ...column, note: value };
                            }

                            return column;

                        });

                        return {
                            ...worksheet,
                            worksheetColumns: updatedColumns
                        };
                    });
                    return {
                        ...prevTemplate,
                        worksheets: updatedWorksheets,
                    };
                });

            },
            [currentTemplate, currentWorksheetId]
        );


        const handleCellValueChange = useCallback((event: { data: { id: number }, newValue: string }) => {
            handleCustomNameChange(event.data.id, event.newValue);
        }, [handleCustomNameChange]);

        const handleNoteCellValueChange = useCallback((event: { data: { id: number }, newValue: string }) => {
            handleNoteChange(event.data.id, event.newValue);
        }, [handleNoteChange]);

        const handleDelete = useCallback((id: number) => {
            if (!currentTemplate) return;

            setCurrentTemplate((prevTemplate) => {
                if (!prevTemplate) return prevTemplate;

                const updatedWorksheets = prevTemplate.worksheets.map(worksheet => {
                    if (worksheet.id !== currentWorksheetId) return worksheet;

                    const updatedColumns = worksheet.worksheetColumns?.filter(column => column.id !== id)
                        .slice()
                        .sort((a, b) => a.order - b.order)
                        .map((col, index) => ({
                            ...col,
                            order: index + 1
                        })
                        );

                    return {
                        ...worksheet,
                        worksheetColumns: updatedColumns,
                    };
                });

                return {
                    ...prevTemplate,
                    worksheets: updatedWorksheets,
                };


            });
        }, [currentTemplate, currentWorksheetId, setCurrentTemplate]);

        const handleReorder = useCallback((reOrderedRows: IRowDataIdListOrder[]) => {

            if (!currentTemplate) return;

            setCurrentTemplate(prevTemplate => {
                if (!prevTemplate) return prevTemplate;

                const rowOrderMap = new Map(reOrderedRows.map(row => [row.id, row.order]));

                const updatedWorksheets = prevTemplate.worksheets.map(worksheet => {
                    const updatedColumns = worksheet.worksheetColumns?.map(col => ({
                        ...col,
                        order: rowOrderMap.get(col.id) ?? col.order,
                    }));

                    return {
                        ...worksheet,
                        worksheetColumns: updatedColumns,
                    };
                });

                return {
                    ...prevTemplate,
                    worksheets: updatedWorksheets,
                };
            });

        }, [currentTemplate]);

        const handleRowReorder = useCallback((fromId: number, toIndex: number) => {

            if (!rowData || toIndex < 0 || toIndex >= rowData.length)
                return;

            // Find the original row object using id.value
            const originalRow = rowData.find(row => row.id.value === fromId);

            if (!originalRow)
                return;

            // Find the original index
            const fromIndex = rowData.findIndex(d => d.id.value === fromId);

            if (fromIndex === toIndex)
                return;

            // Remove the item from its old position
            rowData.splice(fromIndex, 1);
            rowData.splice(toIndex, 0, originalRow);

            const idList: IRowDataIdListOrder[] = rowData.map((item, index) => ({
                id: item.id?.value,
                order: index + 1
            }));

            handleReorder(idList);

        }, [rowData, handleReorder]);


        const handleRowDragEnd = useCallback((event: RowDragEndEvent<ISecuritiesTableFieldMap>) => {

            if (!rowData || !event.node)
                return;

            const movingNode = event.node;
            const toIndex = event.overIndex;

            if (!movingNode.data)
                return;

            const fromId = movingNode.data.id as unknown as number;

            handleRowReorder(fromId, toIndex);
        }, [rowData, handleRowReorder]);

        const handleSimulatedRowDragEnd = useCallback((params: { rowId: number; rowIndex: number }) => {
            const { rowId, rowIndex } = params;
            console.log('Simulated row drag end', rowId, rowIndex);
            handleRowReorder(rowId, rowIndex);
        }, [handleRowReorder]);

        const initializeGridOptions = useCallback(() => {

            setGridOptions({
                columnDefs: [
                    { field: 'id', headerName: '', hide: true },
                    { field: 'fieldIsRequired', headerName: '', hide: true },
                    {
                        headerName: '',
                        field: 'drag',
                        rowDrag: true,
                        maxWidth: 50
                    }
                    , { field: 'order', headerName: 'Order', editable: false, maxWidth: 90 },
                    {
                        // Not editable: CustomCellRenderer already renders its own persistent,
                        // always-visible <input>, so ag-grid's built-in edit mode isn't needed.
                        // Previously (editable: true + onCellClicked calling startEditingCell),
                        // clicking the cell activated ag-grid's own default text editor on top of
                        // CustomCellRenderer, so keystrokes went into that overlay editor and only
                        // committed on blur/Tab/Enter. Removing onCellClicked/startEditingCell too:
                        // with editable: false, api.setFocusedCell() (called via that handler) was
                        // moving ag-grid's managed focus onto the non-editable cell wrapper right
                        // after the click, immediately stealing DOM focus back from our <input> and
                        // making it appear to briefly focus, then become uneditable.
                        field: 'customName', headerName: 'Custom Name', editable: false, minWidth: 170
                        , onCellValueChanged: handleCellValueChange
                        , cellRenderer: CustomCellRenderer
                        // Belt-and-suspenders alongside CustomCellRenderer's own onKeyDown/
                        // stopPropagation: since this column no longer enters ag-grid's edit mode,
                        // keys ag-grid handles for cell navigation/actions (arrows, Space, Delete,
                        // Backspace, etc.) would otherwise be intercepted by the grid before/instead
                        // of the native input, breaking keyboard editing. Scoped to only the
                        // embedded input (via its className) so arrow-key navigation still works
                        // when focus is on the cell wrapper itself (e.g. just after tabbing/arrowing
                        // into this column) instead of trapping grid keyboard navigation entirely.
                        , suppressKeyboardEvent: (params) =>
                            (params.event.target as HTMLElement)?.classList.contains('custom-cell-renderer-input')
                    }
                    // Bound to fieldName (the immutable original Field.Name), not name/customName,
                    // so this column always reflects the true field identity regardless of any
                    // Custom Name saved on this worksheet column. Requires the backend to populate
                    // fieldName from the joined Field record on every load (see AB#1141705).
                    , { field: 'fieldName', headerName: 'Field Name', editable: false, sortable: false, filter: false, minWidth: 150 }
                    , {
                        field: 'mapToField', headerName: 'Database Mapping', editable: false, sortable: false, filter: false, minWidth: 150
                        , tooltipValueGetter: (params) => params.value ?? ''
                    }
                    , {
                        field: 'validationInfoDisplay', headerName: 'Instructions', editable: false, sortable: false, filter: false, minWidth: 220
                        , cellRenderer: ValidationInfoCellRenderer
                        , cellRendererParams: { truncateLength: DEFAULT_VALIDATION_INFO_TRUNCATE_LENGTH }
                    }
                    , {
                        field: 'note', headerName: 'Notes', editable: false, sortable: false, filter: false, minWidth: 220
                        , onCellValueChanged: handleNoteCellValueChange
                        , cellRenderer: NoteCellRenderer
                        , cellRendererParams: { truncateLength: DEFAULT_NOTE_TRUNCATE_LENGTH }
                    }
                    , {
                        field: 'actions', headerName: 'Actions'
                        , cellRenderer: ActionCellRenderer
                        , cellRendererParams: (params: ICellRendererParams) => ({
                            id: params.data.id,
                            isRequired: params.data.fieldIsRequired,
                            handleDelete: handleDelete,
                        })
                        , sortable: false
                        , filter: false
                        , maxWidth: 90
                    }
                    , {
                        colId: 'noteIndicator', field: 'note', headerName: ''
                        , cellRenderer: NoteIndicatorCellRenderer
                        , sortable: false
                        , filter: false
                        , resizable: false
                        , maxWidth: 50
                    },
                ],
                defaultColDef: {
                    sortable: false,
                    filter: false,
                    editable: false,
                    minWidth: 200,
                    cellStyle: {
                        padding: '4px',
                        fontWeight: 'normal',
                        fontFamily: 'Roboto, sans-serif',
                        fontSize: '14px'
                    },
                    flex: 1,
                    resizable: true,
                },
                rowHeight: 52,
                rowDragManaged: true,
                suppressMoveWhenRowDragging: false,
                animateRows: true,
                stopEditingWhenCellsLoseFocus: true,
                onRowDragEnd: handleRowDragEnd,
                onSimulatedRowDragEnd: handleSimulatedRowDragEnd,
                onFirstDataRendered: (params) => {
                    params.api.refreshCells({ force: true })
                }
            })

        }, [handleDelete, handleCellValueChange, handleNoteCellValueChange, handleRowDragEnd, handleSimulatedRowDragEnd]);

        //
        useEffect(() => {

            if (tableRef.current) {
                initializeGridOptions();
            }
        }, [initializeGridOptions]);


        useEffect(() => {
            if (worksheetColumns && worksheetColumns.length > 0) {
                buildRows();
            }
        }, [worksheetColumns, buildRows]);


        // Compute isDirty by comparing current state with original
        const isDirty = React.useMemo(() => {

            if (!currentTemplate || !lastSavedTemplate) return false;

            return JSON.stringify(currentTemplate) !== JSON.stringify(lastSavedTemplate);
        }, [currentTemplate, lastSavedTemplate]);

        const isCurrentTemplateDirty = React.useMemo(() => {

            if (!currentTemplate || !currentOriginalTemplate) return false;

            return JSON.stringify(currentTemplate) !== JSON.stringify(currentOriginalTemplate);
        }, [currentTemplate, currentOriginalTemplate]);


        // Notify parent component when dirty state changes
        useEffect(() => {
            onDirtyChange?.(isDirty);
        }, [isDirty, onDirtyChange]);


        // Reset changes
        const resetChanges = useCallback(() => {

            if (isCurrentTemplateDirty && !window.confirm('Discard changes?')) {
                return false;
            }

            if (currentOriginalTemplate) {
                setCurrentTemplate(JSON.parse(JSON.stringify(currentOriginalTemplate))); // Deep copy
            }

            return true;
        }, [currentOriginalTemplate, isCurrentTemplateDirty]);

        // Expose methods via ref
        useImperativeHandle(ref, () => ({
            isDirty,
            getCurrentTemplate: () => currentTemplate,
            getCurrentOriginalTemplate: () => currentOriginalTemplate,
            reset: resetChanges,
        }), [isDirty, currentTemplate, currentOriginalTemplate, resetChanges]
        );

        const hasWorksheetColumns = !!worksheetColumns && worksheetColumns.length > 0;

        return (
            <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h5" gutterBottom>
                        Worksheet Columns
                    </Typography>

                    <Button
                        variant="contained"
                        className={classes.button}
                        onClick={() => handleOpenThirdDialog(currentWorksheetId, currentTemplate)}
                        sx={{
                            "&:focus": {
                                backgroundColor: "#253746 !important",
                            },
                        }}>
                        + ADD & REMOVE
                    </Button>
                </div>
                {hasWorksheetColumns ? (
                    <Box sx={{ width: '100%' }}>
                        <SecuritiesDraggableTable
                            ref={tableRef}
                            id="worksheetColumns"
                            gridOptions={gridOptions}
                            rowData={rowData}   
                        />
                    </Box>
                ) : (
                    <Typography>No columns found</Typography>
                )}
            </Paper>
        );

    });

export default TemplateWorksheetColumns;